// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { activeStateTrue } from "@steiner/App/Widgets/SearchEngineOptimization/ActiveState";
import { SeoAppProps, SeoHandlerProps } from "@steiner/App/Widgets/SearchEngineOptimization/Type";
import { Graph, SiteNavigationElement, WithContext } from "@steiner/App/Widgets/SearchEngineOptimization/Type/schema";

/**
 * Modal from src/FrontEnd/modules/content-library/src/Widgets/Header/MainNavigation.tsx
 */
interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overrideTitle: string;
        title: string;
    };
}

/**
 * Modal from src/FrontEnd/modules/content-library/src/Widgets/Header/MainNavigation.tsx
 */
export interface MappedLink {
    title: string;
    url: string;
}

const buildMainNavigationLinks = (
    state: ApplicationState,
    options: {
        navigationLinks: LinkModel[];
        allProductsPageUrl?: string;
    },
): MappedLink[] => {
    const { allProductsPageUrl, navigationLinks } = options;

    const links = navigationLinks
        .map(link => {
            const {
                fields: { destination, overrideTitle, title },
            } = link;

            const { type, value } = destination;

            let mappedLink: MappedLink = {
                url: "",
                title: " ",
            };

            if (type === "Url") {
                mappedLink = {
                    url: value,
                    title,
                };
            } else if (type === "Page") {
                const readonlyPageLink = getPageLinkByNodeId(state, value);
                if (readonlyPageLink) {
                    mappedLink = {
                        url: readonlyPageLink.url,
                        title: readonlyPageLink.title,
                    };

                    if (overrideTitle) {
                        mappedLink.title = overrideTitle;
                    }
                }
            } else if (type === "Category") {
                if (value === emptyGuid) {
                    mappedLink = {
                        url: allProductsPageUrl || "",
                        title: overrideTitle || translate("Products"),
                    };
                } else {
                    const categoryState = getCategoryState(state, value);
                    mappedLink = {
                        url: categoryState?.value?.path || "",
                        title: overrideTitle || categoryState?.value?.shortDescription || "",
                    };
                }
            }

            return mappedLink;
        })
        .filter(i => i);

    return links;
};

/**
 * Build the structured data
 */
const loadSchemaSiteNavigationElement = ({ state, payload, url }: SeoAppProps): Graph => {
    const navigationLinks: LinkModel[] = getWidgetsByPageId(state, getHeader(state).id).find(widget => {
        return widget.type === "Header/MainNavigation";
    })?.fields?.links;

    const links = buildMainNavigationLinks(state, {
        navigationLinks,
        allProductsPageUrl: payload.allProductsPageUrl,
    });

    const graphItems = links?.map(
        (item, index): WithContext<SiteNavigationElement> => ({
            "@context": "https://schema.org",
            "@type": "SiteNavigationElement",
            name: item.title,
            url: url + item.url,
            position: index + 1,
        }),
    );

    return {
        "@context": "https://schema.org",
        "@graph": [...graphItems],
    };
};

export default {
    isActivate: activeStateTrue,
    buildSchema: loadSchemaSiteNavigationElement,
} as SeoHandlerProps;
